import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  UserGroupIcon, AcademicCapIcon, BanknotesIcon, ClockIcon,
  CheckCircleIcon, XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .dash * { box-sizing:border-box; }
  .dash { font-family:'Sora',sans-serif; color:#1a1d2e; animation:dashFade .4s ease both; }
  @keyframes dashFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .dash-welcome { margin-bottom:1.5rem; }
  .dash-welcome-title { font-size:1.5rem; font-weight:700; letter-spacing:-.03em; color:#0f1123; }
  .dash-welcome-sub   { font-size:.78rem; color:#9094a8; margin-top:4px; }

  /* Check-in card */
  .checkin-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
  .checkin-info  { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .checkin-dot   { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .checkin-dot.in  { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .checkin-dot.out { background:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,.2); }
  .checkin-dot.off { background:#d1d5db; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px rgba(16,201,142,.2)} 50%{box-shadow:0 0 0 6px rgba(16,201,142,.05)} }
  .checkin-label { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .checkin-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .checkin-times { display:flex; gap:1.5rem; flex-shrink:0; flex-wrap:wrap; }
  .checkin-time-item  { text-align:center; }
  .checkin-time-label { font-size:.65rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.05em; }
  .checkin-time-val   { font-size:.9rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#1a1d2e; margin-top:2px; }
  .checkin-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .checkin-btn svg { width:15px; height:15px; stroke-width:2; }
  .checkin-btn.green { background:#10c98e; color:white; }
  .checkin-btn.green:hover { background:#0db87e; box-shadow:0 4px 14px rgba(16,201,142,.35); transform:translateY(-1px); }
  .checkin-btn.red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .checkin-btn.red:hover   { background:#ef4444; color:white; }
  .checkin-btn.ghost { background:#f0f2f7; color:#9094a8; }
  .checkin-btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  @media (max-width:768px) { .checkin-card { flex-direction:column; align-items:flex-start; gap:.8rem; } }

  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:16px; padding:1.1rem 1.2rem; border:1px solid #e8ebf4; position:relative; overflow:hidden; cursor:pointer; transition:all .18s; }
  .stat-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.08); }
  .stat-card::after { content:''; position:absolute; top:-10px; right:-10px; width:70px; height:70px; border-radius:50%; opacity:.07; }
  .stat-card.blue::after  { background:#3b6ef8; }
  .stat-card.green::after { background:#10c98e; }
  .stat-card.amber::after { background:#f59e0b; }
  .stat-card.red::after   { background:#ef4444; }
  .s-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .s-icon svg { width:17px; height:17px; stroke-width:1.8; }
  .s-icon.blue  {background:#eef2ff;color:#3b6ef8} .s-icon.green{background:#ecfdf5;color:#10c98e}
  .s-icon.amber {background:#fffbeb;color:#f59e0b} .s-icon.red  {background:#fef2f2;color:#ef4444}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; margin-bottom:4px; }
  .s-value { font-size:1.6rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1; }
  .s-sub   { font-size:.7rem; color:#b0b5cc; margin-top:4px; }

  .charts-row { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1.4rem; }
  .chart-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; }
  .chart-title { font-size:.86rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .chart-sub   { font-size:.72rem; color:#9094a8; margin-bottom:1rem; }

  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-link  { font-size:.74rem; color:#3b6ef8; cursor:pointer; font-weight:600; }
  .panel-link:hover { text-decoration:underline; }

  .list-item { display:flex; align-items:flex-start; gap:10px; padding:11px 18px; border-bottom:1px solid #f5f6fb; transition:background .12s; }
  .list-item:last-child { border-bottom:none; }
  .list-item:hover { background:#fafbff; }
  .list-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-top:5px; }
  .list-dot.blue  { background:#3b6ef8; } .list-dot.green { background:#10c98e; }
  .list-dot.amber { background:#f59e0b; }
  .list-title { font-size:.8rem; font-weight:600; color:#1a1d2e; }
  .list-sub   { font-size:.7rem; color:#9094a8; margin-top:2px; }

  .att-today { display:flex; gap:1rem; padding:14px 18px; }
  .att-today-item { flex:1; background:#fafbff; border-radius:12px; padding:12px; border:1px solid #f0f2f8; text-align:center; }
  .att-today-val   { font-size:1.4rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .att-today-label { font-size:.68rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.06em; margin-top:3px; }

  .progress-wrap { padding:0 18px 14px; }
  .progress-label { display:flex; justify-content:space-between; font-size:.72rem; color:#6b7194; margin-bottom:5px; font-weight:600; }
  .progress-bar { height:8px; background:#f0f2f7; border-radius:99px; overflow:hidden; }
  .progress-fill { height:100%; border-radius:99px; transition:width .6s ease; }

  .dash-loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .custom-tooltip { background:white; border:1px solid #e8ebf4; border-radius:10px; padding:8px 12px; font-family:'Sora',sans-serif; font-size:.76rem; box-shadow:0 4px 16px rgba(0,0,0,.1); }
  .ct-label { font-weight:700; color:#0f1123; margin-bottom:4px; }
  .ct-row   { display:flex; align-items:center; gap:6px; padding:2px 0; color:#6b7194; }
  .ct-dot   { width:8px; height:8px; border-radius:50%; flex-shrink:0; }

  @media (max-width:1100px) { .stat-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:900px)  { .charts-row,.two-col { grid-template-columns:1fr; } }
  @media (max-width:768px)  {
    .dash-welcome-title { font-size:1.2rem; }
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .s-value   { font-size:1.3rem; }
    .att-today { flex-wrap:wrap; }
    .att-today-item { min-width:calc(50% - .5rem); }
  }
  @media (max-width:480px) { .stat-grid { grid-template-columns:1fr 1fr; } }
`;

const fmtMoney = (n) => {
  if (!n) return 'Rs. 0';
  if (n >= 1000000) return `Rs. ${(n/1000000).toFixed(1)}M`;
  if (n >= 1000)    return `Rs. ${(n/1000).toFixed(0)}K`;
  return `Rs. ${n.toLocaleString('en-PK')}`;
};
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { day:'numeric', month:'short' });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="ct-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="ct-row">
          <div className="ct-dot" style={{ background: p.color }} />
          {p.name}: <strong>{typeof p.value === 'number' && p.value > 1000 ? fmtMoney(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }) : '—';

const Dashboard = () => {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [today,     setToday]     = useState(null);
  const [actioning, setActioning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchDashboard(); fetchToday(); }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get('/admin/dashboard');
      setData(res);
    } catch { toast.error('Failed to load dashboard'); }
    finally  { setLoading(false); }
  };

  const fetchToday = async () => {
    try { const { data: res } = await api.get('/admin/own-attendance/today'); setToday(res); } catch { /* silent */ }
  };

  const handleCheckIn = async () => {
    try { setActioning(true); await api.post('/admin/own-attendance/checkin'); toast.success('Checked in!'); fetchToday(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-in failed'); }
    finally { setActioning(false); }
  };
  const handleCheckOut = async () => {
    try { setActioning(true); await api.post('/admin/own-attendance/checkout'); toast.success('Checked out!'); fetchToday(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-out failed'); }
    finally { setActioning(false); }
  };

  if (loading) return (
    <div className="dash"><style>{style}</style>
      <div className="dash-loading"><div className="spinner" />Loading dashboard…</div>
    </div>
  );

  const { stats, weeklyAttendanceChart, feeCollectionChart, studentAttendanceChart, expensesVsAssetsChart, upcomingExams, recentNotices } = data || {};
  const attPct = stats?.totalTeachers ? Math.round((stats.teacherPresent / stats.totalTeachers) * 100) : 0;

  return (
    <div className="dash"><style>{style}</style>

      <div className="dash-welcome">
        <div className="dash-welcome-title">Dashboard 👋</div>
        <div className="dash-welcome-sub">
          {new Date().toLocaleDateString('en-US', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
        </div>
      </div>

      {/* Check-in Card */}
      {today?.checkinEnabled && (() => {
        const rec    = today?.record;
        const hasIn  = !!rec?.checkIn;
        const hasOut = !!rec?.checkOut;
        return (
          <div className="checkin-card">
            <div className="checkin-info">
              <div className={`checkin-dot ${hasOut?'out':hasIn?'in':'off'}`}/>
              <div>
                <div className="checkin-label">{hasOut?'Checked Out':hasIn?'Currently Checked In':'Not Checked In'}</div>
                <div className="checkin-sub">{rec?.lateMinutes>0?`${rec.lateMinutes}m late today`:'Right on time'}</div>
              </div>
            </div>
            {(hasIn||hasOut)&&(
              <div className="checkin-times">
                <div className="checkin-time-item"><div className="checkin-time-label">Check In</div><div className="checkin-time-val">{fmtTime(rec?.checkIn)}</div></div>
                {hasOut&&<div className="checkin-time-item"><div className="checkin-time-label">Check Out</div><div className="checkin-time-val">{fmtTime(rec?.checkOut)}</div></div>}
                {hasOut&&rec?.workHours&&<div className="checkin-time-item"><div className="checkin-time-label">Hours</div><div className="checkin-time-val">{Number(rec.workHours||0).toFixed(1)}h</div></div>}
              </div>
            )}
            <div style={{width:'100%',maxWidth:200}}>
              {!hasIn&&<button className="checkin-btn green" onClick={handleCheckIn} disabled={actioning}><CheckCircleIcon/>{actioning?'Please wait…':'Check In'}</button>}
              {hasIn&&!hasOut&&<button className="checkin-btn red" onClick={handleCheckOut} disabled={actioning}><XCircleIcon/>{actioning?'Please wait…':'Check Out'}</button>}
              {hasOut&&<button className="checkin-btn ghost" disabled>Done for today</button>}
            </div>
          </div>
        );
      })()}

      {/* Stat Cards */}
      <div className="stat-grid">
        <div className="stat-card blue" onClick={() => navigate('/students')}>
          <div className="s-icon blue"><UserGroupIcon /></div>
          <div className="s-label">Total Students</div>
          <div className="s-value">{stats?.totalStudents ?? 0}</div>
          <div className="s-sub">enrolled</div>
        </div>
        <div className="stat-card green" onClick={() => navigate('/teachers')}>
          <div className="s-icon green"><AcademicCapIcon /></div>
          <div className="s-label">Total Teachers</div>
          <div className="s-value">{stats?.totalTeachers ?? 0}</div>
          <div className="s-sub">
            <span style={{color:'#10c98e',fontWeight:600}}>{stats?.teacherPresent ?? 0} present</span> today
          </div>
        </div>
        <div className="stat-card amber" onClick={() => navigate('/leaves')}>
          <div className="s-icon amber"><ClockIcon /></div>
          <div className="s-label">Pending Leaves</div>
          <div className="s-value">{stats?.pendingLeaves ?? 0}</div>
          <div className="s-sub">awaiting approval</div>
        </div>
        <div className="stat-card red" onClick={() => navigate('/fees')}>
          <div className="s-icon red"><BanknotesIcon /></div>
          <div className="s-label">Fee Overdue</div>
          <div className="s-value">{stats?.feeOverdue ?? 0}</div>
          <div className="s-sub">students</div>
        </div>
        <div className="stat-card green">
          <div className="s-icon green"><BanknotesIcon /></div>
          <div className="s-label">Collected This Month</div>
          <div className="s-value" style={{fontSize:'1.1rem'}}>{fmtMoney(stats?.feeThisMonth)}</div>
          <div className="s-sub">fee payments</div>
        </div>
      </div>

      {/* Charts — side by side */}
      <div className="charts-row">
        <div className="chart-panel">
          <div className="chart-title">Teacher Attendance — Last 7 Days</div>
          <div className="chart-sub">Daily present vs absent count</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weeklyAttendanceChart} barSize={18} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
              <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{fontFamily:'Sora',fontSize:11}} />
              <Bar dataKey="present" name="Present" fill="#10c98e" radius={[4,4,0,0]} />
              <Bar dataKey="absent"  name="Absent"  fill="#fecaca" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-panel">
          <div className="chart-title">Fee Collection — Last 6 Months</div>
          <div className="chart-sub">Total paid per month (Rs.)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={feeCollectionChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
              <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={42}
                tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}K`:v} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="total" name="Collected" stroke="#3b6ef8" strokeWidth={2.5}
                dot={{r:4,fill:'#3b6ef8',strokeWidth:0}} activeDot={{r:6,fill:'#3b6ef8'}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts — row 2 */}
      <div className="charts-row">
        <div className="chart-panel">
          <div className="chart-title">Student Attendance — Last 7 Days</div>
          <div className="chart-sub">Daily present vs absent count</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={studentAttendanceChart} barSize={18} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
              <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{fontFamily:'Sora',fontSize:11}} />
              <Bar dataKey="present" name="Present" fill="#10c98e" radius={[4,4,0,0]} />
              <Bar dataKey="absent"  name="Absent"  fill="#fecaca" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-panel">
          <div className="chart-title">Expenses vs Assets — Last 6 Months</div>
          <div className="chart-sub">Monthly expenses vs asset purchases (Rs.)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={expensesVsAssetsChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false} />
              <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={42}
                tickFormatter={v => v>=1000?`${(v/1000).toFixed(0)}K`:v} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{fontFamily:'Sora',fontSize:11}} />
              <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" strokeWidth={2.5}
                dot={{r:4,fill:'#ef4444',strokeWidth:0}} activeDot={{r:6,fill:'#ef4444'}} />
              <Line type="monotone" dataKey="assets" name="Assets" stroke="#8b5cf6" strokeWidth={2.5}
                dot={{r:4,fill:'#8b5cf6',strokeWidth:0}} activeDot={{r:6,fill:'#8b5cf6'}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom panels */}
      <div className="two-col">

        {/* Left: Attendance + Notices */}
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Today's Teacher Attendance</div>
            <span className="panel-link" onClick={() => navigate('/teacher-attendance')}>View all</span>
          </div>
          <div className="att-today">
            <div className="att-today-item">
              <div className="att-today-val" style={{color:'#10c98e'}}>{stats?.teacherPresent ?? 0}</div>
              <div className="att-today-label">Present</div>
            </div>
            <div className="att-today-item">
              <div className="att-today-val" style={{color:'#ef4444'}}>{stats?.teacherAbsent ?? 0}</div>
              <div className="att-today-label">Absent</div>
            </div>
            <div className="att-today-item">
              <div className="att-today-val" style={{color:'#3b6ef8'}}>{stats?.totalTeachers ?? 0}</div>
              <div className="att-today-label">Total</div>
            </div>
          </div>
          <div className="progress-wrap">
            <div className="progress-label">
              <span>Attendance rate</span>
              <span style={{color:'#10c98e',fontFamily:"'JetBrains Mono',monospace"}}>{attPct}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{
                width:`${attPct}%`,
                background: attPct >= 80 ? '#10c98e' : attPct >= 60 ? '#f59e0b' : '#ef4444',
              }}/>
            </div>
          </div>

          <div style={{borderTop:'1px solid #f0f2f8'}}>
            <div className="panel-head" style={{borderBottom:'none',paddingBottom:6}}>
              <div className="panel-title">Recent Notices</div>
              <span className="panel-link" onClick={() => navigate('/notices')}>View all</span>
            </div>
            {!recentNotices?.length ? (
              <div style={{padding:'1rem 18px',fontSize:'.78rem',color:'#9094a8'}}>No notices yet.</div>
            ) : recentNotices.slice(0,3).map(n => (
              <div key={n._id} className="list-item">
                <div className="list-dot amber" />
                <div>
                  <div className="list-title">{n.title}</div>
                  <div className="list-sub">{n.postedBy?.name} · {fmtDate(n.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Exams + Leave summary */}
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Upcoming Exams</div>
            <span className="panel-link" onClick={() => navigate('/datesheets')}>View all</span>
          </div>
          {!upcomingExams?.length ? (
            <div style={{padding:'2rem 18px',textAlign:'center',color:'#9094a8',fontSize:'.78rem'}}>No upcoming exams.</div>
          ) : upcomingExams.map(e => {
            const daysLeft = Math.ceil((new Date(e.date) - new Date()) / 86400000);
            return (
              <div key={e._id} className="list-item" style={{cursor:'pointer'}} onClick={() => navigate('/datesheets')}>
                <div className="list-dot blue" />
                <div style={{flex:1}}>
                  <div className="list-title">{e.subject || e.title}</div>
                  <div className="list-sub">{e.class?.name || 'All classes'} · {fmtDate(e.date)}</div>
                </div>
                <div style={{
                  fontSize:'.68rem', fontWeight:700, padding:'2px 8px', borderRadius:99,
                  background: daysLeft <= 3 ? '#fef2f2' : daysLeft <= 7 ? '#fffbeb' : '#eef2ff',
                  color:      daysLeft <= 3 ? '#ef4444' : daysLeft <= 7 ? '#f59e0b' : '#3b6ef8',
                  whiteSpace:'nowrap', flexShrink:0,
                }}>
                  {daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft}d`}
                </div>
              </div>
            );
          })}

          <div style={{borderTop:'1px solid #f0f2f8',padding:'14px 18px',display:'flex',gap:12,flexWrap:'wrap'}}>
            <div style={{flex:1,textAlign:'center',background:'#fffbeb',borderRadius:10,padding:'10px 8px'}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'1.1rem',fontWeight:700,color:'#f59e0b'}}>{stats?.pendingLeaves ?? 0}</div>
              <div style={{fontSize:'.66rem',color:'#9094a8',fontWeight:600,textTransform:'uppercase',marginTop:2}}>Pending Leaves</div>
            </div>
            <div style={{flex:1,textAlign:'center',background:'#ecfdf5',borderRadius:10,padding:'10px 8px'}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'1.1rem',fontWeight:700,color:'#10c98e'}}>{stats?.approvedLeavesThisMonth ?? 0}</div>
              <div style={{fontSize:'.66rem',color:'#9094a8',fontWeight:600,textTransform:'uppercase',marginTop:2}}>Approved This Month</div>
            </div>
            <div style={{flex:1,textAlign:'center',background:'#fef2f2',borderRadius:10,padding:'10px 8px'}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'1.1rem',fontWeight:700,color:'#ef4444'}}>{stats?.feeOverdue ?? 0}</div>
              <div style={{fontSize:'.66rem',color:'#9094a8',fontWeight:600,textTransform:'uppercase',marginTop:2}}>Fee Overdue</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;